/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Programmatic generator for cubic ice (ice Ic) — a genuine,
 * crystallographically correct ice lattice.
 *
 * Why this exists: ice cannot be written down by hand (or by a language
 * model) reliably. It is not a simple atomic lattice. The oxygens sit
 * on a diamond sublattice, but the hydrogens must obey the
 * Bernal-Fowler ICE RULES:
 *   - every oxygen is covalently bound to exactly two hydrogens;
 *   - every O···O contact is bridged by exactly one hydrogen.
 * A proton arrangement that violates those rules is not at the force
 * field's energy minimum, so molecular dynamics relaxes it away from
 * crystalline — which is exactly the "drips away from crystalline"
 * symptom seen with hand-typed / AI-generated ice.
 *
 * This builder constructs the real thing: oxygens on the 8 diamond
 * sites of the conventional cubic cell, hydrogens placed by a
 * deterministic proton-ordered assignment that satisfies the ice rules
 * EXACTLY, with each water at the TIP4P/Ice geometry (O-H 0.9572 Å,
 * H-O-H 104.52°). Cubic ice is real ice — same tetrahedral H-bond
 * network and density as ordinary hexagonal ice, just a different
 * stacking — and a cubic cell tiles cleanly under periodic boundaries.
 */
import { Vector3 } from 'three';
import { CrystalState } from '../types';

/** Cubic lattice constant — tuned so O-O = 2.77 Å, density 0.92 g/cm³. */
const ICE_IC_A = 6.4;
/** O-H bond length (Å), TIP4P/Ice. */
const OH_BOND = 0.9572;
/** Half the H-O-H angle (rad), TIP4P/Ice 104.52°. */
const HOH_HALF_ANGLE = (104.52 / 2) * (Math.PI / 180);

export const buildCubicIce = (): CrystalState => {
  // Eight oxygens on the diamond lattice of the conventional cubic
  // cell. Indices 0-3 are sublattice A, 4-7 are sublattice B; every A
  // oxygen bonds to all four B oxygens.
  const oxygen: Vector3[] = [
    new Vector3(0, 0, 0),
    new Vector3(0, 0.5, 0.5),
    new Vector3(0.5, 0, 0.5),
    new Vector3(0.5, 0.5, 0),
    new Vector3(0.25, 0.25, 0.25),
    new Vector3(0.25, 0.75, 0.75),
    new Vector3(0.75, 0.25, 0.75),
    new Vector3(0.75, 0.75, 0.25),
  ];
  // The four tetrahedral A→B bond directions, as fractional offsets.
  const tetra: Vector3[] = [
    new Vector3(0.25, 0.25, 0.25),
    new Vector3(0.25, -0.25, -0.25),
    new Vector3(-0.25, 0.25, -0.25),
    new Vector3(-0.25, -0.25, 0.25),
  ];
  // Locate an oxygen by fractional position — everything sits on a
  // quarter-cell grid, so round ×4 and wrap into [0,4).
  const wrap4 = (x: number) => ((Math.round(x * 4) % 4) + 4) % 4;
  const oxygenAt = new Map<string, number>();
  oxygen.forEach((p, i) => oxygenAt.set(`${wrap4(p.x)},${wrap4(p.y)},${wrap4(p.z)}`, i));

  // For each oxygen, the unit directions of the two bonds whose
  // hydrogen it owns. Filled by the ice-rules assignment below.
  const owned: Vector3[][] = oxygen.map(() => []);

  // Walk the 16 O-O bonds (each A oxygen to its four B neighbors) and
  // assign the bridging hydrogen.
  //
  // The assignment must do two things: (1) satisfy the ice rules — each
  // oxygen owns exactly two hydrogens; (2) give the unit cell ZERO net
  // dipole. Point (2) is essential under periodic boundaries: a cell
  // with a net dipole carries a macroscopic polarization, and a
  // cutoff-based electrostatics method then sees a spurious depolarizing
  // field that drives every molecule out of place. (A ferroelectric,
  // all-dipoles-aligned proton order is a real ice phase — ice XI — but
  // it is exactly what must NOT be used in a periodic simulation.)
  //
  // `aOwnsOddBond[i]` says whether A-oxygen i owns its odd-indexed or
  // even-indexed B bonds. This particular table is the antiferroelectric
  // solution: it is ice-rules-valid AND the eight molecular dipoles
  // cancel exactly — the A-sublattice dipoles come out ±y, the
  // B-sublattice dipoles ±z, each pair summing to zero.
  const aOwnsOddBond = [false, true, true, false];
  for (let ai = 0; ai < 4; ai++) {
    for (const t of tetra) {
      const nb = new Vector3(oxygen[ai].x + t.x, oxygen[ai].y + t.y, oxygen[ai].z + t.z);
      const bIndex = oxygenAt.get(`${wrap4(nb.x)},${wrap4(nb.y)},${wrap4(nb.z)}`);
      if (bIndex === undefined) continue; // unreachable for the diamond lattice
      const bj = bIndex - 4;
      const dir = new Vector3(t.x, t.y, t.z).normalize();
      if ((bj % 2 === 1) === aOwnsOddBond[ai]) {
        owned[ai].push(dir.clone()); // hydrogen on the A oxygen, points A→B
      } else {
        owned[bIndex].push(dir.clone().multiplyScalar(-1)); // on the B oxygen, points B→A
      }
    }
  }

  // Emit the P1 basis: each oxygen plus its two hydrogens at the exact
  // TIP4P/Ice geometry. The two hydrogens straddle the bisector of the
  // oxygen's two owned bond directions, splayed to H-O-H = 104.52°.
  const basis: CrystalState['basis'] = [];
  const fracPerAngstrom = 1 / ICE_IC_A; // cubic cell → uniform scale
  const cosHalf = Math.cos(HOH_HALF_ANGLE);
  const sinHalf = Math.sin(HOH_HALF_ANGLE);
  oxygen.forEach((o, i) => {
    basis.push({ element: 'O', fx: o.x, fy: o.y, fz: o.z, label: `O${i + 1}` });
    const [d1, d2] = owned[i];
    const bisector = d1.clone().add(d2).normalize();
    const inPlane = d1.clone().sub(d2).normalize();
    const hDir1 = bisector.clone().multiplyScalar(cosHalf).addScaledVector(inPlane, sinHalf);
    const hDir2 = bisector.clone().multiplyScalar(cosHalf).addScaledVector(inPlane, -sinHalf);
    [hDir1, hDir2].forEach((hd, k) => {
      basis.push({
        element: 'H',
        fx: o.x + OH_BOND * fracPerAngstrom * hd.x,
        fy: o.y + OH_BOND * fracPerAngstrom * hd.y,
        fz: o.z + OH_BOND * fracPerAngstrom * hd.z,
        label: `H${i * 2 + k + 1}`,
      });
    });
  });

  return {
    // Distinct name so it imports cleanly alongside any older 'Ice'
    // card already saved in a project (the gallery de-dups by name).
    name: 'Cubic Ice',
    lattice: { a: ICE_IC_A, b: ICE_IC_A, c: ICE_IC_A, alpha: 90, beta: 90, gamma: 90 },
    spaceGroup: 'P 1',
    basis,
    molecular: true,
  };
};
